import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminApi, errorMessage } from './adminApi';
import { useQueryParams } from './useQueryParams';
import { formatDateTime, formatRelative } from './format';
import {
  Badge, DataTable, Drawer, ListLayout, Pagination, Panel, Spinner, Toolbar,
  btnDanger, btnPrimary, btnSecondary, inputClass
} from './AdminUI';

const PAGE_SIZE = 15;
const author = (user) => (user ? `${user.name || 'Unknown'}${user.unit != null ? ` (unit ${user.unit})` : ''}` : 'Unknown');
const snippet = (text, length = 90) => (text && text.length > length ? `${text.slice(0, length)}…` : text || '');

function LinkTicket({ condo, post, onLinked, onCancel }) {
  const api = useAdminApi();
  const [q, setQ] = useState('');
  const [tickets, setTickets] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      api.get(`/api/admin/condos/${condo._id}/tickets`, { page: 1, limit: 10, status: 'open', q: q || undefined })
        .then((response) => setTickets(response.data.tickets || []))
        .catch(() => setTickets([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [api, condo._id, q]);

  const link = async (ticket) => {
    try {
      await api.put(`/api/forum/${post._id}/link-ticket`, { ticketId: ticket._id });
      onLinked('Ticket linked to the post.');
    } catch (err) {
      alert(errorMessage(err, 'Failed to link ticket'));
    }
  };

  return (
    <div className="mt-3 p-3 rounded border border-border space-y-2">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search open tickets..." aria-label="Search open tickets" className={inputClass} autoFocus />
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
      {!tickets ? <p className="text-xs text-muted-foreground">Loading tickets...</p> : tickets.length === 0 ? (
        <p className="text-xs text-muted-foreground">No open tickets found.</p>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {tickets.map((ticket) => (
            <li key={ticket._id}>
              <button type="button" onClick={() => link(ticket)} className="w-full text-left px-3 py-2 text-sm rounded border border-border hover:bg-secondary/50">
                <span className="font-medium text-foreground">{[ticket.ticketNumber, ticket.title].filter(Boolean).join(' ')}</span>
                <span className="ml-2 text-xs text-muted-foreground capitalize">{ticket.priority}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PostDrawer({ condo, post, onClose, onChanged, notify }) {
  const api = useAdminApi();
  const [linking, setLinking] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (action, message, fallback) => {
    setBusy(true);
    try {
      await action();
      notify(message);
      onChanged();
    } catch (err) {
      alert(errorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const createTicket = () => {
    if (!window.confirm('Create a ticket from this forum post?')) return;
    run(() => api.post(`/api/forum/${post._id}/create-ticket`, {}), 'Ticket proposed from the post. Review it in the Approvals tab.', 'Failed to create ticket');
  };

  const removePost = () => {
    if (!window.confirm('Delete this post and all its comments?')) return;
    run(async () => {
      await api.del(`/api/forum/${post._id}`);
      onClose();
    }, 'Post deleted.', 'Failed to delete post');
  };

  const removeComment = (comment) => {
    if (!window.confirm('Delete this comment?')) return;
    run(() => api.del(`/api/forum/${post._id}/comments/${comment._id}`), 'Comment deleted.', 'Failed to delete comment');
  };

  return (
    <Drawer
      title={author(post.user)}
      badges={<>{post.ticket && <Badge className="text-primary border-primary">Ticket linked</Badge>}{post.approval?.status === 'pending' && <Badge className="text-warning border-warning">Ticket pending approval</Badge>}</>}
      subtitle={<>{formatDateTime(post.createdAt)} · <Link to={`/${condo.slug}/forum/${post._id}`} className="text-primary hover:underline">View as resident</Link></>}
      onClose={onClose}
    >
      <p className="text-sm text-foreground whitespace-pre-wrap">{post.content}</p>
      {post.images?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {post.images.map((image, i) => (
            <img key={i} src={image.url || image} alt={`Attachment ${i + 1}`} className="w-full h-24 object-cover rounded border border-border" />
          ))}
        </div>
      )}
      {post.ticket && <p className="text-sm mt-3"><span className="text-muted-foreground">Ticket:</span> {post.ticket.title}</p>}
      <div className="flex flex-wrap gap-2 mt-4">
        {!post.ticket && !post.approval && (
          <>
            <button type="button" onClick={createTicket} disabled={busy} className={btnPrimary}>Create ticket</button>
            <button type="button" onClick={() => setLinking(!linking)} className={btnSecondary}>Link ticket</button>
          </>
        )}
        <button type="button" onClick={removePost} disabled={busy} className={`${btnDanger} ml-auto`}>Delete post</button>
      </div>
      {linking && <LinkTicket condo={condo} post={post} onCancel={() => setLinking(false)} onLinked={(message) => { setLinking(false); notify(message); onChanged(); }} />}
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-foreground mb-2">Comments ({post.comments?.length || 0})</h3>
        {(post.comments || []).length === 0 ? <p className="text-sm text-muted-foreground">No comments.</p> : (
          <ul className="space-y-3">
            {post.comments.map((comment) => (
              <li key={comment._id} className="border-l-2 border-border pl-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{author(comment.user)}</span>
                  <span>{formatRelative(comment.createdAt)}</span>
                  <button type="button" onClick={() => removeComment(comment)} disabled={busy} className="ml-auto text-destructive hover:text-destructive/80">Delete</button>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap mt-1">{comment.content}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Drawer>
  );
}

function ForumTab({ condo, notify, onCountsChange }) {
  const api = useAdminApi();
  const [params, setParams] = useQueryParams();
  const [data, setData] = useState(null);

  const pageParam = params.get('page');
  const selectedId = params.get('post');

  const load = useCallback(async () => {
    try {
      const query = pageParam ? { page: pageParam } : selectedId ? { postId: selectedId } : { page: 1 };
      const response = await api.get(`/api/forum/condo/${condo._id}`, query);
      setData(response.data);
    } catch (err) {
      alert(errorMessage(err, 'Failed to fetch forum posts'));
    }
  // Only page changes reload; opening a post on the same page uses what's loaded.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, condo._id, pageParam]);

  useEffect(() => { load(); }, [load]);

  const selected = data?.posts.find((p) => String(p._id) === String(selectedId));

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL forum posts in "${condo.name}"? This cannot be undone.`)) return;
    try {
      const response = await api.del(`/api/admin/condos/${condo._id}/forum-posts`);
      notify(response.data.message);
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to clear forum posts'));
    }
  };

  const columns = [
    { key: 'author', header: 'Author', className: 'whitespace-nowrap font-medium text-foreground', render: (p) => author(p.user) },
    { key: 'content', header: 'Post', render: (p) => snippet(p.content) },
    { key: 'comments', header: 'Comments', hideBelow: 'md', render: (p) => (p.comments || []).length },
    { key: 'ticket', header: 'Ticket', hideBelow: 'xl', render: (p) => (p.ticket ? 'Linked' : p.approval?.status === 'pending' ? 'Pending' : '—') },
    { key: 'createdAt', header: 'Posted', hideBelow: 'md', className: 'whitespace-nowrap text-muted-foreground', render: (p) => formatRelative(p.createdAt) }
  ];

  const drawer = selected ? (
    <PostDrawer condo={condo} post={selected} notify={notify} onChanged={() => { load(); onCountsChange(); }} onClose={() => setParams({ post: null })} />
  ) : null;

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-auto">Forum posts{data ? ` (${data.totalPosts})` : ''}</h2>
          {process.env.NODE_ENV === 'development' && data?.totalPosts > 0 && (
            <button type="button" onClick={clearAll} className={btnDanger}>Clear all posts</button>
          )}
        </Toolbar>
        {!data ? <Spinner /> : (
          <>
            <DataTable
              label="Forum posts"
              columns={columns}
              rows={data.posts}
              selectedId={selectedId}
              onRowClick={(p) => setParams({ post: p._id })}
              emptyMessage="No forum posts yet."
            />
            <Pagination page={data.page} pages={data.totalPages} total={data.totalPosts} limit={PAGE_SIZE} onChange={(n) => setParams({ page: n, post: null })} />
          </>
        )}
      </Panel>
    </ListLayout>
  );
}

export default ForumTab;
